import { Link } from 'react-router-dom';
import { DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'models-download', label: "Models didn't finish downloading" },
  { id: 'missing-source', label: 'A source shows as missing' },
  { id: 'speech-badge', label: 'Speech "not analyzed" / a badge' },
  { id: 're-index', label: 'Re-index after a new build' },
];

export default function Body() {
  return (
    <>
      <DocSection id="models-download" title="Models didn't finish downloading">
        <p>
          A stalled first-run download almost always finishes on its own — ClipRadar retries
          automatically, up to four attempts with backoff between tries, so a single network blip
          doesn't permanently fail a model.
        </p>
        <p>
          A half-downloaded model is also detected and never mistaken for an installed one, so a
          reopened app won't silently run with a broken model — it just resumes the download. If a
          model still looks stuck, open <span style={kbd}>Settings → Models</span> and check its
          status: Downloaded, Downloading, or Failed with a <span style={kbd}>Retry</span> button.
          Models come from HuggingFace and GitHub, so a blocked host, VPN, or restrictive firewall
          can stall one family without erroring the rest of the app.
        </p>
      </DocSection>

      <DocSection id="missing-source" title="A source shows as missing">
        <p>
          A file shown as missing means ClipRadar can't find it where it last saw it — not that the
          analysis was lost. Because analysis data is linked by file content, not by its path,
          relinking is instant once ClipRadar can see the file again.
        </p>
        <p>
          Right-click the row and choose <span style={kbd}>Relink missing file…</span>, then point
          ClipRadar at the file's new location. Imported folders that have moved or gone offline
          show the same way — in red — in the Media sidebar.
        </p>
        <DocShot slug="analysis-and-files" shot="relink" alt="The Relink missing file dialog" />
        <p>
          See <Link to="/docs/concepts/analyzing-media#content-linking">Files linked by content,
          not path</Link> for why relinking doesn't re-run analysis.
        </p>
      </DocSection>

      <DocSection id="speech-badge" title='Speech "not analyzed" / a badge'>
        <p>
          A speech-problem badge in place of a green Speech check means the audio track is damaged
          or unreadable — not that transcription crashed. Amber means usable but worth checking,
          red means unusable, and a neutral badge means there's no audio track at all.
        </p>
        <p>
          Click the badge to open a popover with the exact problem and your options:{' '}
          <span style={kbd}>Re-transcribe now</span>, <span style={kbd}>Create repaired copy</span>,{' '}
          <span style={kbd}>View transcript</span>, or <span style={kbd}>Looks fine — dismiss</span>.
        </p>
        <DocShot slug="analysis-and-files" shot="speech-badge" alt="A speech-problem badge on a media row" />
        <p>
          For the full walkthrough of each option, see{' '}
          <Link to="/docs/guides/fix-speech-problems#badge-meaning">Fix speech & transcript problems</Link>.
        </p>
      </DocSection>

      <DocSection id="re-index" title="Re-index after a new build">
        <p>
          When ClipRadar ships a new visual model, sources analyzed with the older one are quietly
          held back from Visuals search rather than searched with mismatched embeddings — this is
          expected, not a bug, and only affects the handful of sources it flags.
        </p>
        <p>
          Those sources get a <span style={kbd}>Re-index for New Model</span> entry in their row
          menu. Run it once per affected source and Visuals search covers it again. See{' '}
          <Link to="/docs/concepts/analyzing-media#re-index">Re-index for a new model</Link> for
          how ClipRadar decides which sources need it.
        </p>
      </DocSection>
    </>
  );
}
